import WarehouseNameTooltip from '../../components/WarehouseNameTooltip';
import { calculatePack } from './inventoryMeasurements';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { buildInventorySummary, FieldSelect, formFromInventory, inventoryPayload, validateForm } from './InventoryModal';

const InventoryInlineRows = forwardRef(function InventoryInlineRows({ item, lookups, onSave, onCancel, onToast, initialCell, onPreview, hideLocationColumns = false, locationColumnsLast = false, showCartonMeasurements = false }, ref) {
  const [activeCell, setActiveCell] = useState(initialCell);
  const [openSelect, setOpenSelect] = useState(() => ['Warehouse', 'Customer'].includes(initialCell) ? initialCell : null);
  const [selectQueries, setSelectQueries] = useState({});
  const [draft, setDraft] = useState(() => formFromInventory(item, lookups));
  const [saving, setSaving] = useState(false);
  const [totalInputs, setTotalInputs] = useState({});
  useEffect(() => {
    function cancelOnEscape(event) {
      if (event.key !== 'Escape' || event.isComposing || saving) return;
      event.preventDefault();
      event.stopPropagation();
      onCancel();
    }
    document.addEventListener('keydown', cancelOnEscape, true);
    return () => document.removeEventListener('keydown', cancelOnEscape, true);
  }, [onCancel, saving]);
  useEffect(() => {
    if (!onPreview) return;
    const original = buildInventorySummary(formFromInventory(item, lookups), lookups);
    const edited = buildInventorySummary(draft, lookups);
    onPreview({ id: item.id, gross: edited.gross - original.gross,
      net: edited.net - original.net, volume: edited.volume - original.volume,
      cartons: edited.cartons - original.cartons, products: edited.products - original.products });
  }, [draft, item, lookups, onPreview]);
  useLayoutEffect(() => {
    if (!activeCell) return;
    const field = Array.from(document.querySelectorAll('.inv-cell-edit-row [aria-label]'))
      .find(element => element.getAttribute('aria-label') === activeCell);
    field?.focus({ preventScroll: true });
  }, [activeCell]);
  const rows = draft.packaging.flatMap((pack, pi) => pack.batches.flatMap((batch, bi) => batch.products.map((product, ri) => ({ pack, batch, product, pi, bi, ri }))));
  function change(path, value, numeric = false) {
    if (numeric && !/^\d*\.?\d*$/.test(value)) return;
    setDraft(current => {
      const next = JSON.parse(JSON.stringify(current));
      let target = next;
      path.slice(0, -1).forEach(key => { target = target[key]; });
      target[path[path.length - 1]] = value;
      return next;
    });
  }
  const input = (path, value, label, numeric = false) => activeCell !== label ? <button type="button" className="inv-cell-value" onClick={() => setActiveCell(label)}>{value || "?"}</button> : <input className="inv-cell-input" aria-label={label} value={value} disabled={saving} inputMode={numeric ? 'decimal' : undefined} onChange={event => change(path, event.target.value, numeric)} />;
  function changeTotal(pi, field, value) {
    if (!/^\d*\.?\d*$/.test(value)) return;
    setDraft(current => {
      const next = JSON.parse(JSON.stringify(current));
      const pack = next.packaging[pi];
      const level = pack.measurement_level || next.measurement_level || 'Carton';
      if (level === 'Pallet' || pack.use_master_measurements) pack[field] = value;
      else {
        if (level === 'Product') {
          next.packaging[pi] = calculatePack(pack, 'Product');
          next.packaging[pi].measurement_level = 'Carton';
        }
        const targetPack = next.packaging[pi];
        const targets = targetPack.batches;
        const total = targets.reduce((sum, target) => sum + Number(target[field] || 0), 0);
        let assigned = 0;
        targets.forEach((target, index) => {
          const amount = index === targets.length - 1 ? Number(value || 0) - assigned
            : Math.round(Number(value || 0) * (total ? Number(target[field] || 0) / total : 1 / targets.length) * 1e6) / 1e6;
          target[field] = String(Math.max(0, Math.round(amount * 1e6) / 1e6));
          assigned += Number(target[field]);
        });
      }
      if (next.packaging.every(row => row.measurement_level === 'Carton')) next.measurement_level = 'Carton';
      return next;
    });
  }
  function closeSelect() {
    setOpenSelect(null);
    setSelectQueries({});
  }
  const select = (field, options, label, path = [field], value = draft[field]) => {
    const searchable = field === 'warehouse_id' || field === 'customer_id';
    const query = selectQueries[field] || '';
    const selectedOption = options.find(option => String(option.id) === String(value));
    const selectedLabel = field === 'warehouse_id' ? selectedOption?.code || selectedOption?.name : selectedOption?.name;
    const filteredOptions = searchable
      ? options.filter(option => `${field === 'warehouse_id' ? option.code || '' : ''} ${option.name}`.toLowerCase().includes(query.trim().toLowerCase()))
      : options;
    return activeCell !== label
      ? <WarehouseNameTooltip name={field === 'warehouse_id' ? selectedOption?.name : undefined}><button type="button" className="inv-cell-value" onFocus={() => { setActiveCell(label); setSelectQueries({}); setOpenSelect(label); }} onClick={() => { setActiveCell(label); setSelectQueries({}); setOpenSelect(label); }}>{selectedLabel || '?'}</button></WarehouseNameTooltip>
      : <div className="inv-cell-select"><FieldSelect compact floating ariaLabel={label} disabled={saving} searchLabel={searchable ? `Search ${field === 'warehouse_id' ? 'warehouses' : 'customers'}` : undefined} showOptionsAfterSearch={searchable} onSearchChange={searchable ? next => setSelectQueries(current => ({ ...current, [field]: next })) : undefined} open={openSelect === label} onToggle={() => {
        if (openSelect === label) closeSelect();
        else { setSelectQueries({}); setOpenSelect(label); }
      }} onClose={closeSelect} value={selectedLabel} placeholder={`Select ${label.toLowerCase()}`}>
      <div className="inv-cell-select-options">
        {filteredOptions.length ? filteredOptions.map(option => <WarehouseNameTooltip key={option.id} name={field === 'warehouse_id' ? option.name : undefined}><button type="button" role="option" aria-selected={String(option.id) === String(value)} className={`wh-form-option${String(option.id) === String(value) ? ' selected' : ''}`} onClick={() => { change(path, option.id); closeSelect(); }}>{field === 'warehouse_id' ? option.code || option.name : option.name}</button></WarehouseNameTooltip>)
          : <p className="inv-cell-select-empty">No matching {field === 'warehouse_id' ? 'warehouses' : 'customers'}.</p>}
      </div>
    </FieldSelect></div>;
  };
  async function save() {
    if (saving) return;
    const errors = validateForm(draft, false, lookups);
    if (Object.keys(errors).length) { onToast?.(Object.values(errors)[0], 'error'); return; }
    setSaving(true);
    try { await onSave(inventoryPayload(draft, lookups)); onCancel(); }
    catch (error) { onToast?.(error.message, 'error'); }
    finally { setSaving(false); }
  }
  useImperativeHandle(ref, () => ({ save, saving }));
  return rows.map(({ pack, batch, product, pi, bi, ri }, index) => {
    const packPath = ['packaging', pi];
    const batchPath = [...packPath, 'batches', bi];
    const productPath = [...batchPath, 'products', ri];
    const packSpan = pack.batches.reduce((sum, row) => sum + row.products.length, 0);
    const locationCells = index === 0 && <>
      {!hideLocationColumns && <>
        <td rowSpan={rows.length}>{select('warehouse_id', lookups.warehouses, 'Warehouse')}</td>
        <td rowSpan={rows.length}>{select('customer_id', lookups.customers, 'Customer')}</td>
      </>}
      <td rowSpan={rows.length}>{input(['marko'], draft.marko, 'Marko')}</td>
    </>;
    return <tr key={`${pi}-${bi}-${ri}`} className="inv-cell-edit-row" onClick={event => {
      if (saving || event.target.closest('button, input, .inv-cell-select')) return;
      event.target.closest('td')?.querySelector('.inv-cell-value')?.click();
    }}>
      {index === 0 && <>
        <td rowSpan={rows.length} className="inv-actions-cell">
          <div className="inv-inline-actions">
            <button type="button" className="inv-icon-action" title="Save Changes" aria-label="Save Changes" disabled={saving} onClick={save}><Check size={15} /></button>
            <button type="button" className="inv-icon-action danger" title="Cancel" aria-label="Cancel" disabled={saving} onClick={onCancel}><X size={15} /></button>
          </div>
        </td>
      </>}
      {!locationColumnsLast && locationCells}
      {bi === 0 && ri === 0 && <>
        <td rowSpan={packSpan}>{select('packaging_type_id', lookups.packaging_types, `Packaging type ${pi + 1}`, [...packPath, 'packaging_type_id'], pack.packaging_type_id)}</td>
        <td rowSpan={packSpan}>{input([...packPath, 'packaging_quantity'], pack.packaging_quantity, `Package quantity ${pi + 1}`, true)}</td>
      </>}
      {ri === 0 && <td rowSpan={batch.products.length}>{input([...batchPath, 'batch_quantity'], batch.batch_quantity, `Carton quantity ${pi + 1}-${bi + 1}`, true)}</td>}
      <td>{input([...productPath, 'name'], product.name, `Product name ${index + 1}`)}</td>
      {['quantity', 'gross_weight', 'net_weight'].map(field => <td key={field}>{input([...productPath, field], product[field], `Product ${field} ${index + 1}`, true)}</td>)}
      {showCartonMeasurements && ri === 0 && ['gross_weight', 'net_weight', 'cbm'].map(field => <td key={field} rowSpan={batch.products.length}>{(pack.use_master_measurements && Number(pack[field]) > 0 ? 0 : Number(batch[field] || 0)).toLocaleString('en-US', { maximumFractionDigits: field === 'cbm' ? 3 : 2 })}</td>)}
      {bi === 0 && ri === 0 && <>{['gross_weight', 'net_weight', 'cbm'].map(field => {
        const label = `Packaging ${field} ${pi + 1}`;
        const value = calculatePack(pack, pack.measurement_level || draft.measurement_level)[field] || 0;
        return <td key={field} rowSpan={packSpan}>{activeCell === label
          ? <input className="inv-cell-input" aria-label={label} value={totalInputs[label] ?? value} disabled={saving} inputMode="decimal" onChange={event => {
            const next = event.target.value;
            if (!/^\d*\.?\d*$/.test(next)) return;
            setTotalInputs(current => ({ ...current, [label]: next }));
            changeTotal(pi, field, next);
          }} />
          : <button type="button" className="inv-cell-value" onClick={() => {
            setTotalInputs(current => ({ ...current, [label]: String(value) }));
            setActiveCell(label);
          }}>{Number(value).toLocaleString()}</button>}</td>;
      })}</>}
      {locationColumnsLast && locationCells}
    </tr>;
  });
});
export default InventoryInlineRows;
